import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Search, Plus, X, Mail, Phone, MapPin, Car, User, Pencil } from "lucide-react";

/**
 * Reusable customer picker.
 *
 * Props:
 *   value    : string          currently-selected contact_id
 *   onChange : (contact|null)  called with the selected contact object
 *                              (contains name, email, phone, address, vehicle) or null on clear
 *   required : boolean         when true, submit is blocked until a contact is picked
 *   testId   : string          data-testid prefix (default "contact-picker")
 *   allowNew : boolean         allow inline creation of a new contact (default true)
 */
export default function ContactPickerField({
  value,
  onChange,
  required = false,
  testId = "contact-picker",
  allowNew = true,
  label = "Customer",
}) {
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [newModalOpen, setNewModalOpen] = useState(false);

  const loadContacts = async () => {
    try {
      const { data } = await api.get("/contacts");
      setContacts(data);
      return data;
    } catch {
      setContacts([]);
      return [];
    }
  };

  useEffect(() => { loadContacts(); }, []);

  // Whenever the parent-provided value or contacts list changes, resync selected
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    const found = contacts.find((c) => c.id === value);
    if (found) setSelected(found);
  }, [value, contacts]);

  const handlePick = (contact) => {
    setSelected(contact);
    setPickerOpen(false);
    onChange?.(contact);
  };

  const handleClear = () => {
    setSelected(null);
    onChange?.(null);
  };

  const handleCreated = async (newContact) => {
    // Refresh list, select the freshly-created one, and close both modals
    const list = await loadContacts();
    const picked = list.find((c) => c.id === newContact.id) || newContact;
    setSelected(picked);
    onChange?.(picked);
    setNewModalOpen(false);
    setPickerOpen(false);
  };

  return (
    <div>
      <label className="text-label block mb-1.5">
        {label} {required && <span className="text-red-400">*</span>}
      </label>

      {selected ? (
        <div
          className="rounded-2xl border border-white/10 bg-white/[0.04] p-3.5 flex items-start gap-3"
          data-testid={`${testId}-selected`}
        >
          <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center shrink-0">
            <User className="w-4 h-4 text-slate-200" />
          </div>
          <div className="flex-1 min-w-0 space-y-0.5">
            <div className="text-sm text-white font-medium truncate" data-testid={`${testId}-name`}>{selected.name}</div>
            <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-400">
              {selected.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{selected.email}</span>}
              {selected.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{selected.phone}</span>}
              {selected.vehicle && <span className="flex items-center gap-1"><Car className="w-3 h-3" />{selected.vehicle}</span>}
              {selected.address && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{selected.address}</span>}
            </div>
          </div>
          <div className="flex gap-1 shrink-0">
            <button
              type="button"
              data-testid={`${testId}-change`}
              onClick={() => setPickerOpen(true)}
              className="text-[11px] px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 flex items-center gap-1"
            >
              <Pencil className="w-3 h-3" /> Change
            </button>
            {!required && (
              <button
                type="button"
                data-testid={`${testId}-clear`}
                onClick={handleClear}
                className="p-1 rounded-full bg-white/5 border border-white/10 text-slate-400 hover:bg-red-500/20 hover:text-red-300"
                title="Clear"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid={`${testId}-open`}
          onClick={() => setPickerOpen(true)}
          className="w-full flex items-center justify-center gap-2 rounded-2xl border border-dashed border-white/15 bg-white/[0.02] text-slate-300 hover:bg-white/[0.05] hover:border-white/30 py-3.5 text-sm transition"
        >
          <Search className="w-4 h-4" />
          Select or add customer
        </button>
      )}

      {pickerOpen && createPortal(
        <ContactPickerModal
          contacts={contacts}
          onPick={handlePick}
          onAddNew={allowNew ? () => setNewModalOpen(true) : null}
          onClose={() => setPickerOpen(false)}
          testId={testId}
        />,
        document.body
      )}

      {newModalOpen && createPortal(
        <NewContactModal
          onClose={() => setNewModalOpen(false)}
          onCreated={handleCreated}
          testId={testId}
        />,
        document.body
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------- */
/*                            Picker modal (browse)                             */
/* ---------------------------------------------------------------------------- */
function ContactPickerModal({ contacts, onPick, onAddNew, onClose, testId }) {
  const [q, setQ] = useState("");
  const filtered = contacts.filter((c) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [c.name, c.email, c.phone, c.vehicle].some((f) => (f || "").toLowerCase().includes(s));
  });

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
      <div
        className="glass-strong rounded-3xl p-6 w-full max-w-md max-h-[85vh] flex flex-col"
        data-testid={`${testId}-modal`}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl text-white">Select customer</h2>
          <button
            type="button"
            data-testid={`${testId}-modal-close`}
            onClick={onClose}
            className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-slate-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            data-testid={`${testId}-search`}
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, phone, email…"
            className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
          />
        </div>

        <div className="flex-1 overflow-y-auto -mx-2 px-2 space-y-1.5">
          {filtered.length === 0 && (
            <div className="text-center text-slate-500 italic text-sm py-8">
              {contacts.length === 0 ? "No contacts yet." : "No matches."}
            </div>
          )}
          {filtered.map((c) => (
            <button
              type="button"
              key={c.id}
              data-testid={`${testId}-option-${c.id}`}
              onClick={() => onPick(c)}
              className="w-full text-left rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-white/20 p-3 transition"
            >
              <div className="text-sm text-white font-medium">{c.name}</div>
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-400 mt-0.5">
                {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                {c.vehicle && <span className="flex items-center gap-1"><Car className="w-3 h-3" />{c.vehicle}</span>}
              </div>
            </button>
          ))}
        </div>

        {onAddNew && (
          <button
            type="button"
            data-testid={`${testId}-add-new`}
            onClick={onAddNew}
            className="mt-4 w-full flex items-center justify-center gap-2 bg-white text-black rounded-full py-2.5 text-sm font-semibold hover:bg-slate-100"
          >
            <Plus className="w-4 h-4" /> Add new contact
          </button>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------- */
/*                          New contact modal (create)                          */
/* ---------------------------------------------------------------------------- */
function NewContactModal({ onClose, onCreated, testId }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", vehicle: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    // required: name, phone, email
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Name, phone and email are required");
      return;
    }
    // email format
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) {
      toast.error("Please enter a valid email address");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/contacts", form);
      toast.success("Contact added");
      onCreated?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add contact");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[65] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
      <div
        className="glass-strong rounded-3xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto space-y-4"
        data-testid={`${testId}-new-modal`}
        onKeyDown={(e) => {
          // Prevent Enter from bubbling out and submitting the parent form
          if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
            e.preventDefault();
            e.stopPropagation();
            submit();
          }
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl text-white">Add new contact</h2>
            <p className="text-[11px] text-slate-500 mt-0.5">Saved to Contacts and available anywhere in the app.</p>
          </div>
          <button
            type="button"
            data-testid={`${testId}-new-modal-close`}
            onClick={onClose}
            className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-slate-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <MiniField
          label="Full name"
          required
          value={form.name}
          onChange={(v) => setForm({ ...form, name: v })}
          testId={`${testId}-new-name`}
        />
        <MiniField
          label="Phone"
          required
          value={form.phone}
          onChange={(v) => setForm({ ...form, phone: v })}
          testId={`${testId}-new-phone`}
        />
        <MiniField
          label="Email"
          type="email"
          required
          value={form.email}
          onChange={(v) => setForm({ ...form, email: v })}
          testId={`${testId}-new-email`}
        />

        <div className="pt-2 border-t border-white/5">
          <div className="text-label mb-2">Optional details</div>
          <div className="space-y-3">
            <MiniField
              label="Address"
              value={form.address}
              onChange={(v) => setForm({ ...form, address: v })}
              testId={`${testId}-new-address`}
            />
            <div>
              <label className="text-label block mb-1.5">Notes</label>
              <textarea
                data-testid={`${testId}-new-notes`}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm text-slate-300 hover:bg-white/10">
            Cancel
          </button>
          <button
            data-testid={`${testId}-new-save`}
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-5 py-2.5 rounded-full bg-white text-black text-sm font-semibold hover:bg-slate-100 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save contact"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MiniField({ label, value, onChange, required = false, type = "text", testId, placeholder }) {
  return (
    <div>
      <label className="text-label block mb-1.5">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <input
        data-testid={testId}
        type={type}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
      />
    </div>
  );
}
